import { useEffect, useRef } from 'react';

interface FeatureBaseWidgetProps {
  /**
   * FeatureBase organization slug - defaults to your organization
   */
  organization?: string;
  /**
   * Whether to show the default feedback button
   */
  showButton?: boolean;
  /**
   * Custom user data to pass to FeatureBase
   */
  user?: {
    mail?: string;
    id?: string | number;
    name?: string;
    img?: string;
  };
  /**
   * Additional metadata to pass to FeatureBase
   */
  metadata?: Record<string, any>;
  /**
   * Extra horizontal offset from the right edge (in pixels) when using bottom-right placement
   */
  offsetRightPx?: number;
}

declare global {
  interface Window {
    Featurebase: any;
  }
}

export function FeedbackWidget({ 
  organization = 'emwave',
  showButton = true,
  user,
  metadata,
  offsetRightPx = 160
}: FeatureBaseWidgetProps) {
  const scriptLoadedRef = useRef(false);
  const widgetInitializedRef = useRef(false);
  const observerRef = useRef<MutationObserver | null>(null);

  const applyBottomRightOffset = () => {
    try {
      const iframes = Array.from(document.querySelectorAll<HTMLIFrameElement>('iframe[src*="featurebase.app"], iframe[src*="do.featurebase.app"]'));
      for (const iframe of iframes) {
        const rect = iframe.getBoundingClientRect();
        const cs = window.getComputedStyle(iframe);
        const isFixed = cs.position === 'fixed' || iframe.style.position === 'fixed';
        const looksLikeBubble = rect.width <= 120 && rect.height <= 120;
        if (isFixed && looksLikeBubble) {
          // Apply to iframe
          iframe.style.setProperty('left', 'auto', 'important');
          iframe.style.setProperty('transform', 'none', 'important');
          iframe.style.setProperty('right', `${Math.max(24, offsetRightPx)}px`, 'important');
          iframe.style.setProperty('bottom', '20px', 'important');
          iframe.style.setProperty('top', 'auto', 'important');
          // Also try to apply to container in case it controls position
          const parent = iframe.parentElement as HTMLElement | null;
          if (parent) {
            parent.style.setProperty('left', 'auto', 'important');
            parent.style.setProperty('transform', 'none', 'important');
            parent.style.setProperty('right', `${Math.max(24, offsetRightPx)}px`, 'important');
            parent.style.setProperty('bottom', '20px', 'important');
            parent.style.setProperty('top', 'auto', 'important');
            parent.style.setProperty('position', 'fixed', 'important');
          }
        }
      }
    } catch {}
  };

  useEffect(() => {
    // Prevent multiple script loads
    if (scriptLoadedRef.current) return;

    // Set up FeatureBase queue function (official pattern from docs)
    const win = window as any;
    if (typeof win.Featurebase !== "function") {
      win.Featurebase = function () {
        // eslint-disable-next-line prefer-rest-params
        (win.Featurebase.q = win.Featurebase.q || []).push(arguments);
      };
    }

    // Create and inject the FeatureBase script - use proxy in development
    const script = document.createElement('script');
    script.src = import.meta.env.DEV ? '/featurebase-sdk/js/sdk.js' : 'https://do.featurebase.app/js/sdk.js';
    script.async = true;
    script.id = 'featurebase-sdk';
    
    // Mark as loaded when script loads
    script.onload = () => {
      scriptLoadedRef.current = true;
      console.log('FeatureBase SDK loaded successfully');
      
      // Load CSS through proxy to avoid CORS
      if (import.meta.env.DEV) {
        const cssLink = document.createElement('link');
        cssLink.rel = 'stylesheet';
        cssLink.href = '/featurebase-sdk/js/sdk.css';
        document.head.appendChild(cssLink);
        console.log('FeatureBase CSS loaded through proxy');

        // Prevent SDK from loading CSS directly (which causes CORS issues)
        const originalCreateElement = document.createElement;
        document.createElement = function(tagName: string) {
          const element = originalCreateElement.call(this, tagName);
          if (tagName.toLowerCase() === 'link') {
            // Intercept link elements created by FeatureBase SDK
            const originalSetAttribute = element.setAttribute;
            element.setAttribute = function(name: string, value: string) {
              if (name === 'href' && value.includes('do.featurebase.app/js/sdk.css')) {
                console.log('Intercepted FeatureBase CSS load, skipping to prevent CORS');
                return; // Skip setting the href to prevent the load
              }
              return originalSetAttribute.call(this, name, value);
            };
          }
          return element;
        };
      }
      
      // Initialize widget using official pattern (only once)
      if (!widgetInitializedRef.current) {
        win.Featurebase("initialize_feedback_widget", {
          organization: organization,
          theme: "light",
          placement: showButton ? "bottom-right" : undefined, // Native bottom-right placement
          email: user?.mail,
          locale: "en",
          ...(metadata && { metadata })
        });

        widgetInitializedRef.current = true;
        console.log('FeatureBase widget initialized with config:', {
          organization,
          showButton,
          userEmail: user?.mail
        });

        // Adjust horizontal offset from the right and keep it applied on DOM changes
        setTimeout(applyBottomRightOffset, 0);
        observerRef.current?.disconnect();
        observerRef.current = new MutationObserver(() => applyBottomRightOffset());
        observerRef.current.observe(document.body, { childList: true, subtree: true });
        window.addEventListener('resize', applyBottomRightOffset);
      } else {
        console.log('FeatureBase widget already initialized, skipping...');
      }
    };

    script.onerror = (error) => {
      console.error('Failed to load FeatureBase SDK:', error);
    };

    document.getElementsByTagName('head')[0].appendChild(script);

    // Cleanup function
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      window.removeEventListener('resize', applyBottomRightOffset);
    };
  }, [organization, showButton, user, metadata, offsetRightPx]); // Re-run if these props change

  // Update user/metadata when they change
  useEffect(() => {
    if (scriptLoadedRef.current && widgetInitializedRef.current && (user || metadata)) {
      // Re-initialize widget with new user data
      const win = window as any;
      if (win.Featurebase) {
        win.Featurebase("initialize_feedback_widget", {
          organization: organization,
          theme: "light",
          placement: showButton ? "bottom-right" : undefined,
          email: user?.mail,
          locale: "en",
          ...(metadata && { metadata })
        });
        setTimeout(applyBottomRightOffset, 0);
        console.log('FeatureBase widget re-initialized with updated user data');
      }
    }
  }, [user, metadata, organization, showButton, offsetRightPx]);

  // This component doesn't render anything visible - the widget is injected by FeatureBase
  return null;
}

export default FeedbackWidget;
